import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Box, Chip, Pagination, Paper, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { site } from 'apis';
import { useEffect, useMemo, useState } from 'react';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import SiteAvatarImage from './Components/SiteAvatarImage';
import { CompetitionDateBlock } from '../utils/calendarDates';
import { competitionTypeChipSx, getDisciplineChipSx } from '../utils/chipStyles';
import { getCompetitionBanner, getCompetitionBannerSrc, getCompetitionId } from '../utils/competitionBanners';
import { getGoogleMapsApiKey } from '../utils/googleMaps';
import { getCountryFlagAlt, getCountryFlagSrc } from 'utils/countryFlags';

const COMPETITIONS_PER_PAGE = 20;

const getSiteAddress = (siteItem: any) =>
    [siteItem?.address1, siteItem?.address2, siteItem?.zipCode, siteItem?.city].filter(Boolean).join(', ');

const getCompetitionField = (competition: any, camelKey: string, pascalKey: string) => competition?.[camelKey] ?? competition?.[pascalKey];

const mapContainerStyle = {
    width: '100%',
    height: '100%'
};

const toValidCoordinatePair = (latValue?: string, lngValue?: string) => {
    const lat = latValue === null || latValue === '' ? NaN : Number(latValue);
    const lng = lngValue === null || lngValue === '' ? NaN : Number(lngValue);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    if (lat === 0 && lng === 0) return null;

    return { lat, lng };
};

const getMapOptions = () => ({
    fullscreenControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    zoomControl: true
});

const getWebsiteHref = (value?: string) => {
    if (!value) return '';
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const InfoRow = ({ label, value, icon, href, external }: { label: string; value?: any; icon?: any; href?: string; external?: boolean }) => {
    if (!value) return null;

    return (
        <TableRow>
            <TableCell sx={{ width: 135, background: '#f2f2f2', fontWeight: 700, py: 1 }}>{label}</TableCell>
            <TableCell sx={{ fontWeight: 700, color: '#075aaa', py: 1 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                    {icon}
                    {href ? (
                        <Box
                            component="a"
                            href={href}
                            target={external ? '_blank' : undefined}
                            rel={external ? 'noopener noreferrer' : undefined}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                overflowWrap: 'anywhere',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            {value}
                        </Box>
                    ) : (
                        <span>{value}</span>
                    )}
                </Stack>
            </TableCell>
        </TableRow>
    );
};

const SiteDetailsView = () => {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [competitionsPage, setCompetitionsPage] = useState(1);

    const siteQuery = useQuery(['publicSite', id], () => site.getPublicSite({ id }), {
        enabled: !!id,
        suspense: false
    });

    const siteData = siteQuery.data;
    const competitions = useMemo(() => (Array.isArray(siteData?.competitions) ? siteData.competitions : []), [siteData]);
    const coordinates = useMemo(() => toValidCoordinatePair(siteData?.latitude, siteData?.longitude), [siteData]);
    const googleMapsApiKey = useMemo(() => getGoogleMapsApiKey(), []);
    const competitionsPageCount = Math.max(1, Math.ceil(competitions.length / COMPETITIONS_PER_PAGE));
    const competitionsPageStartIndex = (competitionsPage - 1) * COMPETITIONS_PER_PAGE;
    const paginatedCompetitions = competitions.slice(competitionsPageStartIndex, competitionsPageStartIndex + COMPETITIONS_PER_PAGE);
    const showingCompetitionsStart = competitions.length ? competitionsPageStartIndex + 1 : 0;
    const showingCompetitionsEnd = Math.min(competitionsPageStartIndex + paginatedCompetitions.length, competitions.length);
    const competitionBannerQuery = useQuery(
        ['publicSiteCompetitionBanners', paginatedCompetitions.map((item: any) => getCompetitionId(item)).filter(Boolean).join('|')],
        async () => {
            const bannerEntries = await Promise.all(
                paginatedCompetitions.map(async (item: any) => {
                    const competitionId = getCompetitionId(item);
                    if (!competitionId) return null;

                    try {
                        const banner = await getCompetitionBanner(competitionId);
                        return banner ? [String(competitionId).toUpperCase(), banner] : null;
                    } catch (error) {
                        console.error('Error loading competition banner', error);
                        return null;
                    }
                })
            );

            return Object.fromEntries(bannerEntries.filter(Boolean) as any);
        },
        {
            enabled: paginatedCompetitions.length > 0 && !siteQuery.isLoading,
            suspense: false
        }
    );

    useEffect(() => {
        setCompetitionsPage(1);
    }, [siteData?.id]);

    useEffect(() => {
        if (competitionsPage > competitionsPageCount) {
            setCompetitionsPage(competitionsPageCount);
        }
    }, [competitionsPage, competitionsPageCount]);

    const goToCompetition = (competition: any) => {
        const competitionId = getCompetitionField(competition, 'id', 'Id');
        if (!competitionId) return;

        navigate({
            pathname: '/public/competition',
            search: createSearchParams({ id: competitionId }).toString()
        });
    };

    const handleCompetitionsPageChange = (_event: any, page: number) => {
        setCompetitionsPage(page);
    };

    if (siteQuery.isLoading) {
        return (
            <Box sx={{ width: '100%', px: { xs: 2, md: 5 }, py: 3 }}>
                <Typography sx={{ fontWeight: 700 }}>{t('Loading')}</Typography>
            </Box>
        );
    }

    if (!siteData) {
        return (
            <Box sx={{ width: '100%', px: { xs: 2, md: 5 }, py: 3 }}>
                <Typography sx={{ fontWeight: 700 }}>{t('NoDataFound')}</Typography>
            </Box>
        );
    }

    const countryFlagSrc = getCountryFlagSrc(siteData);

    return (
        <Box sx={{ width: '100%', px: { xs: 2, md: 5 }, py: 3 }}>
            <Stack spacing={3} sx={{ maxWidth: 1280, mx: 'auto' }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box sx={{ width: 3, height: 30, background: '#ffe000' }} />
                    <Typography variant="h2" sx={{ fontWeight: 500, color: '#172033' }}>
                        {siteData.name}
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: coordinates && googleMapsApiKey ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))'
                        },
                        gap: 3,
                        alignItems: 'stretch',
                        width: '100%'
                    }}
                >
                    <Paper
                        sx={{
                            width: '100%',
                            height: 'auto',
                            minHeight: { md: 340 },
                            overflow: 'visible',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
                            display: 'block'
                        }}
                    >
                        <Table
                            size="small"
                            sx={{
                                height: 'auto',
                                '& .MuiTableRow-root': {
                                    height: 42
                                }
                            }}
                        >
                            <TableBody>
                                <InfoRow
                                    label={t('Country')}
                                    value={siteData.countryAbbr || siteData.countryName}
                                    icon={
                                        countryFlagSrc ? (
                                            <Box
                                                component="img"
                                                src={countryFlagSrc}
                                                alt={getCountryFlagAlt(siteData)}
                                                sx={{
                                                    width: 28,
                                                    height: 20,
                                                    objectFit: 'cover',
                                                    border: '1px solid #d7e0ea',
                                                    borderRadius: 0.5
                                                }}
                                            />
                                        ) : null
                                    }
                                />
                                <InfoRow label={t('Code')} value={siteData.code} />
                                <InfoRow label={t('Address')} value={getSiteAddress(siteData)} />
                                <InfoRow label={t('Contact')} value={siteData.contactName} />
                                <InfoRow label={t('Latitude')} value={siteData.latitude} />
                                <InfoRow label={t('Longitude')} value={siteData.longitude} />
                                <InfoRow
                                    label={t('Phone')}
                                    value={siteData.contactPhone}
                                    icon={<LocalPhoneIcon sx={{ fontSize: 16, color: '#111' }} />}
                                />
                                <InfoRow
                                    label={t('Email')}
                                    value={siteData.contactEmail}
                                    href={`mailto:${siteData.contactEmail}`}
                                    icon={<EmailIcon sx={{ fontSize: 16, color: '#111' }} />}
                                />
                                <InfoRow
                                    label={t('Website')}
                                    value={siteData.websiteUrl}
                                    href={getWebsiteHref(siteData.websiteUrl)}
                                    external
                                    icon={<LanguageIcon sx={{ fontSize: 16, color: '#111' }} />}
                                />
                            </TableBody>
                        </Table>
                    </Paper>

                    <SiteAvatarImage
                        siteId={siteData.id}
                        alt={siteData.name}
                        iconSize={72}
                        sx={{
                            width: '100%',
                            height: { xs: 220, md: 260 },
                            minHeight: 0,
                            maxHeight: { md: 260 },
                            alignSelf: 'flex-start',
                            borderRadius: 1
                        }}
                    />

                    {coordinates && googleMapsApiKey && (
                        <Box
                            sx={{
                                width: '100%',
                                height: { xs: 240, md: '100%' },
                                minHeight: { md: 340 },
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid #d7e0ea',
                                background: '#eef3f7'
                            }}
                        >
                            <LoadScript googleMapsApiKey={googleMapsApiKey}>
                                <GoogleMap mapContainerStyle={mapContainerStyle} center={coordinates} zoom={15} options={getMapOptions()}>
                                    <Marker position={coordinates} />
                                </GoogleMap>
                            </LoadScript>
                        </Box>
                    )}
                </Box>

                <Paper sx={{ p: 2, borderRadius: 1, boxShadow: '0 4px 16px rgba(15, 23, 42, 0.12)' }}>
                    <Typography sx={{ textAlign: 'center', fontWeight: 500, mb: 2 }}>{t('Competitions')}</Typography>
                    <Stack spacing={1.25}>
                        {competitions.length > 0 && (
                            <Typography sx={{ color: '#475569', fontWeight: 700 }}>
                                {showingCompetitionsStart}-{showingCompetitionsEnd} / {competitions.length}
                            </Typography>
                        )}

                        {paginatedCompetitions.map((competition: any) => {
                            const competitionId = getCompetitionId(competition);
                            const banner = competitionBannerQuery.data?.[String(competitionId).toUpperCase()];
                            const bannerSrc = banner?.src || getCompetitionBannerSrc(competition);

                            return (
                                <Box
                                    key={competitionId || getCompetitionField(competition, 'id', 'Id')}
                                    onClick={() => goToCompetition(competition)}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            md: bannerSrc ? '150px 128px minmax(0, 1fr)' : '150px minmax(0, 1fr)'
                                        },
                                        gap: 2,
                                        alignItems: 'center',
                                        p: 2,
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 1.5,
                                        backgroundColor: '#fff',
                                        cursor: 'pointer',
                                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
                                        '&:nth-of-type(odd)': { backgroundColor: '#f8fafc' },
                                        '&:hover': {
                                            boxShadow: '8px 12px 24px rgba(15, 23, 42, 0.12), 0 4px 10px rgba(77, 121, 188, 0.14)',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    <CompetitionDateBlock item={competition} locale={i18n.language} />

                                    {bannerSrc && (
                                        <Box
                                            component="img"
                                            src={bannerSrc}
                                            alt={banner?.alt || competition?.name || competition?.Name || ''}
                                            sx={{
                                                width: { xs: '100%', md: 128 },
                                                maxWidth: { xs: '100%', md: 128 },
                                                height: { xs: 76, sm: 86, md: 'auto' },
                                                aspectRatio: { xs: 'auto', md: '16 / 9' },
                                                borderRadius: { xs: 1.25, md: 1.5 },
                                                border: '1px solid #e5e7eb',
                                                objectFit: { xs: 'cover', md: 'contain' },
                                                backgroundColor: '#fff'
                                            }}
                                        />
                                    )}

                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8, flexWrap: 'wrap' }}>
                                            {getCompetitionField(competition, 'discipline', 'Discipline') && (
                                                <Tooltip
                                                    title={
                                                        getCompetitionField(competition, 'disciplineDescription', 'DisciplineDescription') || ''
                                                    }
                                                    arrow
                                                    disableHoverListener={
                                                        !getCompetitionField(competition, 'disciplineDescription', 'DisciplineDescription')
                                                    }
                                                >
                                                    <Chip
                                                        size="medium"
                                                        label={getCompetitionField(competition, 'discipline', 'Discipline')}
                                                        sx={getDisciplineChipSx(getCompetitionField(competition, 'discipline', 'Discipline'))}
                                                    />
                                                </Tooltip>
                                            )}
                                            {getCompetitionField(competition, 'competitionType', 'CompetitionType') && (
                                                <Tooltip
                                                    title={
                                                        getCompetitionField(
                                                            competition,
                                                            'competitionTypeDescription',
                                                            'CompetitionTypeDescription'
                                                        ) || ''
                                                    }
                                                    arrow
                                                    disableHoverListener={
                                                        !getCompetitionField(
                                                            competition,
                                                            'competitionTypeDescription',
                                                            'CompetitionTypeDescription'
                                                        )
                                                    }
                                                >
                                                    <Chip
                                                        size="medium"
                                                        label={getCompetitionField(competition, 'competitionType', 'CompetitionType')}
                                                        sx={competitionTypeChipSx}
                                                    />
                                                </Tooltip>
                                            )}
                                        </Stack>

                                        <Typography
                                            sx={{
                                                fontSize: { xs: '1rem', md: '1.1rem' },
                                                fontWeight: 800,
                                                lineHeight: 1.25,
                                                color: '#111827',
                                                overflowWrap: 'anywhere'
                                            }}
                                        >
                                            {competition.name || competition.Name || '-'}
                                        </Typography>

                                        {(competition.site || competition.Site || competition.code || competition.Code) && (
                                            <Typography sx={{ color: '#6b7280', fontSize: '0.9rem', mt: 0.4, fontWeight: 600 }}>
                                                {[competition.site || competition.Site, competition.code || competition.Code]
                                                    .filter(Boolean)
                                                    .join(' - ')}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}

                        {!competitions.length && (
                            <Typography sx={{ textAlign: 'center', py: 2 }}>{t('NoCompetitionsFoundinCalendar')}</Typography>
                        )}

                        {competitions.length > COMPETITIONS_PER_PAGE && (
                            <Stack direction="row" justifyContent="center" sx={{ pt: 1 }}>
                                <Pagination
                                    count={competitionsPageCount}
                                    page={competitionsPage}
                                    color="primary"
                                    onChange={handleCompetitionsPageChange}
                                />
                            </Stack>
                        )}
                    </Stack>
                </Paper>
            </Stack>
        </Box>
    );
};

export default SiteDetailsView;
